import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Box, Stack, Typography, Button, CircularProgress, Divider, Chip, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { selectPaymentCategoryTranslation } from 'state/reducers/payment-categories.reducer';
import { DialogBox } from 'components/DialogBox';
import { FormButton } from 'theme/elements/Buttons';
import { IPaymentCategoryTreeProps, IPaymentCategory, IPendingChange } from 'constants/payment-categories.constants';
import { PaymentCategoryNode } from './PaymentCategoryNode';
import { pagesEnum } from 'constants/ui.constants';

// Translation prefix for all keys in this feature
const T_PREFIX = 'page.payment_categories';

// Helper to collect all category IDs recursively for default expansion
const getAllCategoryIds = (categories: IPaymentCategory[]): Record<number, boolean> => {
	const ids: Record<number, boolean> = {};
	const traverse = (cats: IPaymentCategory[]) => {
		cats.forEach((cat) => {
			ids[cat.id] = true;
			if (cat.children?.length) traverse(cat.children);
		});
	};
	traverse(categories);
	return ids;
};

export const PaymentCategoryTree: React.FC<IPaymentCategoryTreeProps> = ({
	categories,
	isLoading,
	onToggleActive,
	hasPendingChanges,
	pendingChangesCount,
	pendingChanges,
	onSave,
	onCancelChanges,
	getPendingStatus,
}) => {
	const theme = useTheme();
	const t = useAppSelector(selectTranslations);
	const categoryTranslation = useAppSelector(selectPaymentCategoryTranslation);
	const navigate = useNavigate();

	// Expanded state for tree nodes
	const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});
	const [initialExpansionDone, setInitialExpansionDone] = useState(false);

	// Initialize with all categories expanded on first load
	useEffect(() => {
		if (categories.length > 0 && !initialExpansionDone) {
			setExpandedIds(getAllCategoryIds(categories));
			setInitialExpansionDone(true);
		}
	}, [categories, initialExpansionDone]);

	// Save confirmation dialog state
	const [saveDialogOpen, setSaveDialogOpen] = useState(false);

	// Toggle expand/collapse for a category
	const handleToggleExpand = useCallback((id: number) => {
		setExpandedIds((prev) => ({
			...prev,
			[id]: !prev[id],
		}));
	}, []);

	// Navigate to edit page
	const handleEdit = useCallback(
		(category: IPaymentCategory) => {
			navigate(`/${pagesEnum.PAYMENT_CATEGORIES}/${category.id}`);
		},
		[navigate]
	);

	// Navigate to add page with parentId param
	const handleAddChild = useCallback(
		(parentId: number) => {
			navigate(`/${pagesEnum.PAYMENT_CATEGORIES}/new?parentId=${parentId}`);
		},
		[navigate]
	);

	// Navigate to add root category page
	const handleAddRoot = useCallback(() => {
		navigate(`/${pagesEnum.PAYMENT_CATEGORIES}/new`);
	}, [navigate]);

	// Handle toggle active
	const handleToggleActive = useCallback(
		(category: IPaymentCategory) => {
			if (onToggleActive) {
				onToggleActive(category.id, !category.isActive);
			}
		},
		[onToggleActive]
	);

	// Open save confirmation dialog
	const handleSaveClick = useCallback(() => {
		setSaveDialogOpen(true);
	}, []);

	// Confirm save
	const handleSaveConfirm = useCallback(() => {
		if (onSave) {
			onSave();
		}
		setSaveDialogOpen(false);
	}, [onSave]);

	// Cancel save dialog
	const handleSaveDialogClose = useCallback(() => {
		setSaveDialogOpen(false);
	}, []);

	// Cancel all changes
	const handleCancelChanges = useCallback(() => {
		if (onCancelChanges) {
			onCancelChanges();
		}
	}, [onCancelChanges]);

	const hasCategories = categories && categories.length > 0;

	// Group pending changes by type for display in save dialog
	const groupedChanges = useMemo(() => {
		if (!pendingChanges || pendingChanges.length === 0) {
			return null;
		}

		const creates = pendingChanges.filter((c) => c.type === 'create');
		const updates = pendingChanges.filter((c) => c.type === 'update');
		const toggles = pendingChanges.filter((c) => c.type === 'toggle');

		return { creates, updates, toggles };
	}, [pendingChanges]);

	// Helper to get category display name using categoryTranslation
	const getCategoryDisplayName = useCallback(
		(change: IPendingChange): string => {
			// For new categories, use the provided name
			if (change.type === 'create' && change.name) {
				return change.name;
			}
			// For updates/toggles, try to get original name from originalValue
			if (change.originalValue) {
				return categoryTranslation(change.originalValue.name);
			}
			// Fallback to the name in change if available
			if (change.name) {
				return categoryTranslation(change.name);
			}
			return `ID: ${change.categoryId}`;
		},
		[categoryTranslation]
	);

	return (
		<Box
			sx={{
				borderRadius: '8px',
				boxShadow: '0 4px 12px 0 rgba(44, 62, 80, 0.16)',
				border: `solid 1px ${theme.palette.root.mediumGreen}`,
				backgroundColor: theme.palette.background.paper,
				padding: 3,
			}}
		>
			{/* Header with action buttons */}
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				marginBottom={2}
				flexWrap="wrap"
				gap={2}
			>
				<Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
					<Typography variant="pageTitle" color={theme.palette.root.formTitle}>
						{t(`${T_PREFIX}.label.title`)}
					</Typography>
					{hasPendingChanges && pendingChangesCount !== undefined && pendingChangesCount > 0 && (
						<Typography
							variant="body2"
							sx={{
								color: theme.palette.warning.main,
								backgroundColor: theme.palette.warning.light,
								padding: '4px 8px',
								borderRadius: '4px',
								whiteSpace: 'nowrap',
							}}
						>
							{t(`${T_PREFIX}.messages.pendingChangesCount`).replace(
								'%%count%%',
								String(pendingChangesCount)
							)}
						</Typography>
					)}
				</Stack>
				<Stack direction="row" spacing={1} flexWrap="wrap">
					<FormButton>
						<Button variant="contained" startIcon={<AddIcon />} onClick={handleAddRoot}>
							{t(`${T_PREFIX}.label.addCategory`)}
						</Button>
					</FormButton>
					{hasPendingChanges && onCancelChanges && (
						<Button
							variant="outlined"
							color="inherit"
							startIcon={<CancelIcon />}
							onClick={handleCancelChanges}
							sx={{
								borderColor: theme.palette.grey[400],
								color: theme.palette.text.secondary,
							}}
						>
							{t(`${T_PREFIX}.button.cancelChanges`)}
						</Button>
					)}
					{onSave && (
						<FormButton>
							<Button
								variant="contained"
								color="primary"
								startIcon={<SaveIcon />}
								onClick={handleSaveClick}
								disabled={!hasPendingChanges}
							>
								{t(`${T_PREFIX}.button.saveChanges`)}
							</Button>
						</FormButton>
					)}
				</Stack>
			</Stack>

			{/* Tree Content */}
			<Box sx={{ position: 'relative' }}>
				{isLoading && (
					<Box
						sx={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: 'rgba(255, 255, 255, 0.7)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							zIndex: 1,
						}}
					>
						<CircularProgress size={32} />
					</Box>
				)}
				{hasCategories ? (
					<Box
						sx={{
							borderTop: `1px solid ${theme.palette.root.divider}`,
							paddingTop: 2,
						}}
					>
						{categories.map((category) => (
							<PaymentCategoryNode
								key={category.id}
								category={category}
								depth={0}
								expandedIds={expandedIds}
								onToggleExpand={handleToggleExpand}
								onEdit={handleEdit}
								onAdd={handleAddChild}
								onToggleActive={onToggleActive ? handleToggleActive : undefined}
								pendingStatus={getPendingStatus ? getPendingStatus(category.id) : undefined}
								getPendingStatus={getPendingStatus}
							/>
						))}
					</Box>
				) : (
					<Box
						sx={{
							borderTop: `1px solid ${theme.palette.root.divider}`,
							paddingTop: 4,
							paddingBottom: 4,
							textAlign: 'center',
						}}
					>
						<Typography variant="body1" color={theme.palette.root.grey}>
							{t(`${T_PREFIX}.messages.noCategories`)}
						</Typography>
					</Box>
				)}
			</Box>

			{/* Save Confirmation Dialog */}
			<DialogBox
				openDialog={saveDialogOpen}
				handleClose={handleSaveDialogClose}
				handleApprove={handleSaveConfirm}
				handleReject={handleSaveDialogClose}
				title={t(`${T_PREFIX}.messages.confirmSaveTitle`)}
				approveLabel={t(`${T_PREFIX}.button.saveChanges`)}
				cancelLabel={t(`${T_PREFIX}.button.cancelChanges`)}
			>
				<Stack spacing={2}>
					<Typography>{t(`${T_PREFIX}.messages.confirmSaveSubtitle`)}</Typography>

					{groupedChanges && (
						<>
							<Divider />
							<Typography variant="subtitle2" fontWeight="bold">
								{t(`${T_PREFIX}.messages.changesSummary`)}:
							</Typography>

							{/* New Categories */}
							{groupedChanges.creates.length > 0 && (
								<Box>
									<Stack direction="row" alignItems="center" spacing={1} marginBottom={1}>
										<FiberNewIcon sx={{ color: theme.palette.success.main, fontSize: '1.2rem' }} />
										<Typography
											variant="body2"
											fontWeight="bold"
											color={theme.palette.success.main}
										>
											{t(`${T_PREFIX}.messages.newCategories`)} ({groupedChanges.creates.length})
										</Typography>
									</Stack>
									<Stack spacing={0.5} paddingLeft={3}>
										{groupedChanges.creates.map((change, index) => (
											<Chip
												key={`create-${index}`}
												size="small"
												label={getCategoryDisplayName(change)}
												sx={{
													width: 'fit-content',
													backgroundColor: theme.palette.success.light,
													color: theme.palette.success.main,
												}}
											/>
										))}
									</Stack>
								</Box>
							)}

							{/* Updated Categories */}
							{groupedChanges.updates.length > 0 && (
								<Box>
									<Stack direction="row" alignItems="center" spacing={1} marginBottom={1}>
										<EditIcon sx={{ color: theme.palette.info.main, fontSize: '1.2rem' }} />
										<Typography variant="body2" fontWeight="bold" color={theme.palette.info.main}>
											{t(`${T_PREFIX}.messages.updatedCategories`)} (
											{groupedChanges.updates.length})
										</Typography>
									</Stack>
									<Stack spacing={0.5} paddingLeft={3}>
										{groupedChanges.updates.map((change, index) => (
											<Typography key={`update-${index}`} variant="body2">
												{getCategoryDisplayName(change)}
												{change.name &&
													change.originalValue &&
													change.name !== change.originalValue.name && (
														<>
															{' → '}
															<Typography
																component="span"
																variant="body2"
																color={theme.palette.info.main}
															>
																{change.name}
															</Typography>
														</>
													)}
											</Typography>
										))}
									</Stack>
								</Box>
							)}

							{/* Toggle Status Changes */}
							{groupedChanges.toggles.length > 0 && (
								<Box>
									<Stack direction="row" alignItems="center" spacing={1} marginBottom={1}>
										<ToggleOnIcon sx={{ color: theme.palette.warning.main, fontSize: '1.2rem' }} />
										<Typography
											variant="body2"
											fontWeight="bold"
											color={theme.palette.warning.main}
										>
											{t(`${T_PREFIX}.messages.toggledCategories`)} (
											{groupedChanges.toggles.length})
										</Typography>
									</Stack>
									<Stack spacing={0.5} paddingLeft={3}>
										{groupedChanges.toggles.map((change, index) => (
											<Typography key={`toggle-${index}`} variant="body2">
												{getCategoryDisplayName(change)}
												{' → '}
												<Typography
													component="span"
													variant="body2"
													color={
														change.isActive
															? theme.palette.success.main
															: theme.palette.error.main
													}
												>
													{change.isActive
														? t(`${T_PREFIX}.messages.activated`)
														: t(`${T_PREFIX}.messages.deactivated`)}
												</Typography>
											</Typography>
										))}
									</Stack>
								</Box>
							)}
						</>
					)}

					{(!groupedChanges || pendingChanges?.length === 0) && (
						<Typography variant="body2" color={theme.palette.text.secondary}>
							{t(`${T_PREFIX}.messages.noChanges`)}
						</Typography>
					)}
				</Stack>
			</DialogBox>
		</Box>
	);
};
